import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import * as AlertDialog from '@givebutter/primitives/alert-dialog';
import { Button } from '@givebutter/primitives/button';
import { useToast } from '@givebutter/primitives/toast';
import BeeFreePlugin from '@beefree.io/sdk';
import {
    BeePluginErrorCodes,
    IAddOnResponseImageWithFields,
    IBeeConfig,
    IMergeTag,
} from '@beefree.io/sdk/dist/types/bee';
import * as Portal from '@radix-ui/react-portal';
import { AccountResponse } from 'api/account';
import { fetchAdvancedEmailToken } from 'api/engage';
import { uploadRemoteMedia } from 'api/media';
import { MergeTagsDialog } from 'assets/js/pages/engage/components/MergeTagsDialog';
import { getAdvancedTemplateScratchTemplate } from 'assets/js/pages/engage/components/constants';
import { CanvaCallbacks, CanvaEditor, useCanva } from 'customHooks/useCanva';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { isImage, isZip } from 'helpers/regex';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { accountQueryKeys } from 'queries/account-query-keys';
import { queryClient } from 'queries/queryClient';

const MINIMUM_WINDOW_SIZE = 1050;
const CONTAINER_ID = 'bee-plugin-container';

interface AdvancedEmailEditorProps {
    template: string;
    onSave: (pageJson: string, pageHtml: string) => void;
}
const AdvancedEmailEditor = ({ template, onSave }: AdvancedEmailEditorProps) => {
    const { accountId, beefreeAuthPayload, beefreeCustomCss } = useCurrentAccount();
    const { advancedMailCanvaIntegration = false } = useFlags();

    const { error: toastError } = useToast();
    const { data: canvaApi } = useCanva();
    const isBuilderInitiated = useRef(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [isBeeLoaded, setIsBeeLoaded] = useState(false);
    const [beeBuilder] = useState(
        beefreeAuthPayload ? new BeeFreePlugin(beefreeAuthPayload) : null,
    );
    const [smallBrowserAlertOpen, setSmallBrowserAlertOpen] = useState(false);

    const mergeTagPromise = useRef<{
        resolve: (data: IMergeTag, options?: Record<string, unknown> | undefined) => void;
        reject: () => void;
    }>();

    const { mutate: fetchAndUpdateToken } = useMutation({
        mutationFn: () => fetchAdvancedEmailToken(accountId),
        onSuccess: response => {
            if (response) {
                queryClient.setQueryData(
                    accountQueryKeys.current(accountId),
                    (oldData: AccountResponse) => {
                        return {
                            ...oldData,
                            menu: {
                                ...oldData.menu,
                                beefree_auth_payload: response,
                            },
                        };
                    },
                );
                beeBuilder?.updateToken(response);
            }
        },
    });

    const builder_config: IBeeConfig = useMemo(
        () => ({
            uid: accountId.toString(),
            container: CONTAINER_ID,
            ...(beefreeCustomCss && { customCss: beefreeCustomCss }),
            onSave: (pageJson, pageHtml) => {
                onSave(pageJson, pageHtml);
            },
            onLoad: () => {
                setIsBeeLoaded(true);
            },
            onError: function (error) {
                if (error.code === BeePluginErrorCodes.EXPIRED_TOKEN_CANNOT_REFRESHED) {
                    fetchAndUpdateToken();
                }
            },
            contentDialog: {
                mergeTags: {
                    label: 'Merge Tags',
                    handler: async (resolve, reject, _args) => {
                        setDialogOpen(true);
                        mergeTagPromise.current = { resolve, reject };
                    },
                },
                addOn: advancedMailCanvaIntegration
                    ? {
                          label: 'Custom Add-Ons',
                          handler: (resolve, reject, args) => {
                              if (args.contentDialogId === 'canvaIntegration') {
                                  const canvaId = (
                                      args.value as IAddOnResponseImageWithFields['value']
                                  ).customFields?.canvaId;

                                  const canvaProps: CanvaEditor & CanvaCallbacks = {
                                      editor: {
                                          fileType: 'jpg',
                                      },
                                      onDesignPublish: async opts => {
                                          // Canva may publish .zip or iframes; we only support images
                                          if (!isImage.exec(opts.exportUrl)) {
                                              const errorMsg = isZip.exec(opts.exportUrl)
                                                  ? 'Please export one Canva page at a time'
                                                  : 'This Canva file type is not supported. Please try again';
                                              toastError(errorMsg);
                                              reject();
                                          }

                                          const { data } = await uploadRemoteMedia(
                                              accountId,
                                              opts.exportUrl,
                                          );

                                          resolve({
                                              type: 'image',
                                              value: {
                                                  alt: 'Canva custom creation',
                                                  href: '',
                                                  src: data.url,
                                                  customFields: {
                                                      canvaId: opts.designId,
                                                  },
                                              },
                                          } as IAddOnResponseImageWithFields);
                                      },
                                      onDesignClose() {
                                          reject();
                                      },
                                  };

                                  if (canvaId) {
                                      canvaApi?.editDesign({
                                          design: {
                                              id: canvaId,
                                          },
                                          ...canvaProps,
                                      });
                                  } else {
                                      canvaApi?.createDesign({
                                          design: {
                                              type: 'Presentation43',
                                          },
                                          ...canvaProps,
                                      });
                                  }
                              }

                              return Promise.resolve();
                          },
                      }
                    : undefined,
            },
        }),
        [
            accountId,
            beefreeCustomCss,
            onSave,
            fetchAndUpdateToken,
            canvaApi,
            toastError,
            advancedMailCanvaIntegration,
        ],
    );

    useEffect(() => {
        if (!beeBuilder || isBuilderInitiated.current) return;
        beeBuilder.start(
            builder_config,
            template ? JSON.parse(template) : getAdvancedTemplateScratchTemplate(),
        );
        isBuilderInitiated.current = true;
    }, [beeBuilder, builder_config, template]);

    const handleResize = useCallback(() => {
        setSmallBrowserAlertOpen(window.innerWidth < MINIMUM_WINDOW_SIZE);
    }, []);

    useEffect(() => {
        const handler = () =>
            setSmallBrowserAlertOpen(isBeeLoaded && window.innerWidth < MINIMUM_WINDOW_SIZE);
        handler();
        window.addEventListener('resize', handler);
        return () => window.removeEventListener('resize', handler);
    }, [handleResize, isBeeLoaded]);

    return (
        <>
            <MergeTagsDialog
                open={dialogOpen}
                onOpenChange={isOpen => setDialogOpen(isOpen)}
                onCancel={() => {
                    mergeTagPromise.current?.reject();
                    setDialogOpen(false);
                }}
                onSelect={tag => {
                    mergeTagPromise.current?.resolve(tag);
                    setDialogOpen(false);
                }}
            />

            <Portal.Root asChild>
                <div className="z-pentOutermost pointer-events-auto fixed inset-0 flex flex-col">
                    <div className="flex w-full justify-end gap-2 border border-b-gray-200 bg-white p-4 py-2">
                        <Button
                            variant="solid"
                            onClick={() => beeBuilder?.save()}
                            disabled={!isBeeLoaded}
                        >
                            Save
                        </Button>
                    </div>
                    <div className="h-full bg-white" id={CONTAINER_ID} />
                </div>
            </Portal.Root>

            <AlertDialog.Root type="warning" open={smallBrowserAlertOpen}>
                <AlertDialog.Portal>
                    <AlertDialog.Overlay />
                    <AlertDialog.Content>
                        <AlertDialog.Header>
                            <AlertDialog.Icon />
                        </AlertDialog.Header>
                        <AlertDialog.Body>
                            <AlertDialog.Title>Your browser is too small.</AlertDialog.Title>
                            <AlertDialog.Description>
                                Increase the size of your browser to a minimum of{' '}
                                {MINIMUM_WINDOW_SIZE}
                                px or use a laptop or desktop computer to continue using the visual
                                email editor.
                            </AlertDialog.Description>
                        </AlertDialog.Body>
                        <AlertDialog.Footer>
                            <AlertDialog.Action
                                onClick={() => {
                                    setSmallBrowserAlertOpen(false);
                                    beeBuilder?.save();
                                }}
                            >
                                Close editor
                            </AlertDialog.Action>
                        </AlertDialog.Footer>
                    </AlertDialog.Content>
                </AlertDialog.Portal>
            </AlertDialog.Root>
        </>
    );
};

export { AdvancedEmailEditor };
